// useSlipSync.js — sync state machine for BetMGM SlipSync
// Spec: docs/specs/slipsync-ui.md Sections 4, 9

import { useState, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '../lib/errorReporting';
import { Capacitor } from '@capacitor/core';
import { authedApiFetch } from '../lib/api';
import { APP_VERSION } from '../lib/constants';
import { openBookWebView } from '../lib/slipsync/cookie-bridge';
import { parseBetslipsMessage } from '../lib/slipsync/betmgm-client';
import { mgmConfig } from '../lib/slipsync/books/mgm';
import { SYNC_STATUS, ERROR_MESSAGES, SUBMIT_TIMEOUT_MS, OPEN_PROBE_POST_TIMEOUT_MS } from '../lib/slipsync/constants';
import { beginAttempt, advanceStage, finishAttempt } from '../lib/attempt-ledger/buffer';
import { flushOwner } from '../lib/attempt-ledger/flush';
import { EXIT, mapExit } from '../lib/slipsync/attempt-exit-map';
import { classifyBookError } from '../lib/slipsync/book-error-class';
import { useAttemptLedgerFlush } from './useAttemptLedgerFlush';
import { track } from '../lib/analytics';
import { EVENTS } from '../lib/analytics-events';
import { isBookEnabled } from '../lib/slipsync/book-availability';
import { check as guardCheck, tryStart, finish as guardFinish, reconnect as guardReconnect, peek as guardPeek, FAILURES_FOR_RECONNECT } from '../lib/slipsync/sync-guard';
import { outcomeClassOf } from '../lib/slipsync/attempt-exit-map';
import { supabase } from '../lib/supabase';
import { readFeatureConsent, recordFeatureConsent, forgetFeatureConsent } from '../features/onboarding/featureConsent';
import { ensureSportsbookSessionOwner } from '../lib/slipsync/session-reset';
import { checkSlipSyncAccess } from '../lib/slipsync/access-check';
import { accessDeniedMessage } from '../lib/accessCopy';

// How many "I agree" saves are in flight across EVERY useSlipSync instance (Codex r4/r5 P1). My Bets mounts one
// settings card per book, so the lock is module-wide: while any agreement saves, no instance starts a sync whose
// consent read could see that grant and open another book.
let consentSavesInFlight = 0;
// Bumped when any instance starts saving an agreement (Codex r8 P1): a consent read that a save overlapped at any
// point, even one that started and finished during the read, is dropped, not only one still overlapping it.
let consentSaveGeneration = 0;

const LS_CONFIG = 'slipsync_config';
const LS_LAST_SYNCED = 'slipsync_last_synced_at';

// String-only: a non-string (e.g. { toString: 'x' } posted by the book page) would
// throw in String() and wedge the sync before any terminal state is set (#1292).
function sanitizeError(err) {
  return (typeof err === 'string' ? err : '').replace(/[^\x20-\x7E]/g, '').slice(0, 200);
}

function formatDiagnostics(diag) {
  if (!Array.isArray(diag) || diag.length === 0) return '';
  return diag.map(d => {
    const parts = [`${d.t}ms ${d.event}`];
    if (d.trigger) parts.push(d.trigger);
    if (d.type) parts.push(d.type);
    if (d.reason) parts.push(d.reason);
    if (d.error) parts.push(sanitizeError(d.error));
    if (d.url) parts.push(d.url);
    if (d.resultType) parts.push(`->${d.resultType}`);
    parts.push(`a=${d.attempts}`);
    return parts.join(' ');
  }).join(' | ');
}

// CC-OBS-003/004: every warning here is one constant message per failure class, the detail in `extra`,
// and a coded `stage` tag. errorReporting forwards a warning to PostHog only when it carries the `fn`
// tag, so an untagged warning reached nothing while app Sentry is off (#1244 audit G4b).
const tagged = (stage, extra, level = 'warning') => ({ level, tags: { function: 'useSlipSync', stage }, ...(extra ? { extra } : {}) });

// The route refuses a body over 512 000 bytes (api/slipsync.js). UTF-8 length without TextEncoder (jsdom lacks it):
// a lone surrogate throws in encodeURIComponent and counts as over the cap.
const SLIPSYNC_MAX_BODY_BYTES = 512000;
function utf8Bytes(str) {
  try { return encodeURIComponent(str).replace(/%[0-9A-F]{2}/g, 'x').length; } catch { return Infinity; }
}

// #1712 PR A (spec §3.3): single job -- post a DK open probe alone when the settled list was empty. Same live-session check
// and token as a submit; its own 15 s bound so the submit-deadline path never runs. Any server answer or
// failure ends as the usual empty sync; a failure is a tagged info message (CC-IMPL-010), never reauth or consent.
async function postOpenProbeOnly(probe, { userId, userIdRef, mountedRef }) {
  const probeAbort = new AbortController();
  const probeTimer = setTimeout(() => probeAbort.abort(), OPEN_PROBE_POST_TIMEOUT_MS);
  const probeDeadline = new Promise((_, reject) => {
    probeAbort.signal.addEventListener('abort', () => reject(new Error('open probe post timed out')));
  });
  probeDeadline.catch(() => {});
  const failed = (status) => Sentry.captureMessage('SlipSync open probe not sent', tagged('open_probe_post_failed', { status }, 'info'));
  try {
    let sessionUserId = null;
    let sessionToken = null;
    try {
      const { data } = await Promise.race([probeDeadline, supabase.auth.getSession()]);
      const sess = data && data.session;
      sessionUserId = sess && sess.user ? sess.user.id : null;
      sessionToken = sess ? sess.access_token || null : null;
    } catch (sessionErr) {
      if (probeAbort.signal.aborted) throw sessionErr;
    }
    if (userIdRef.current !== userId || sessionUserId !== userId || !sessionToken) return 'account_changed';
    const response = await Promise.race([probeDeadline, authedApiFetch('/api/slipsync', {
      signal: probeAbort.signal,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-sliptrack-app-version': APP_VERSION },
      body: JSON.stringify({ book: 'dk', openProbe: probe }),
    }, { accessToken: sessionToken })]);
    if (!response || !response.ok) failed(response ? response.status : null);
    return 'done';
  } catch (probeErr) {
    failed(probeAbort.signal.aborted ? 'timeout' : 'throw');
    return mountedRef.current ? 'done' : 'unmounted';
  } finally {
    clearTimeout(probeTimer);
  }
}

function sendDiagnostics(label, webViewResult) {
  const diagStr = formatDiagnostics(webViewResult.diagnostics);
  if (diagStr) {
    Sentry.captureMessage('SlipSync diagnostics', tagged('diagnostics', { label, diagnostics: diagStr.slice(0, 2000) }));
  }
}

function readConfig(book) {
  try {
    const raw = localStorage.getItem(LS_CONFIG);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // BookSyncConfig contract permits validRegions: string[] | null (spec §5).
    const regions = Array.isArray(book.validRegions) ? book.validRegions : [];
    // New per-book shape: { <book.key>: { region } }
    const region = parsed && parsed[book.key] && parsed[book.key].region;
    if (region && regions.includes(region)) return region;
    // Legacy migration read (mgm only): { betmgm_state }
    if (book.key === 'mgm' && parsed && regions.includes(parsed.betmgm_state)) {
      return parsed.betmgm_state;
    }
    return null;
  } catch {
    Sentry.captureMessage('localStorage read failed for slipsync_config', tagged('config_read'));
    return null;
  }
}

function readLastSynced(book) {
  try {
    const raw = localStorage.getItem(LS_LAST_SYNCED);
    if (!raw) return null;
    // New per-book shape: { <book.key>: iso }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed[book.key] || null;
      return null;
    } catch {
      // Legacy (pre-F2): a bare ISO string. Surface it for mgm only.
      return book.key === 'mgm' ? raw : null;
    }
  } catch {
    Sentry.captureMessage('localStorage read failed for slipsync_last_synced_at', tagged('last_synced_read'));
    return null;
  }
}

// ── Attempt ledger (docs/specs/2026-09-29-attempt-ledger.md §4, §6a) ─────────
// Each helper swallows its own errors: the ledger must never throw into, block or delay the sync.
// Bridge hardening (spec §6): copy templating and countdowns for guard refusals.
const fill = (tpl, vars) => tpl.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
const inSecs = (at) => Math.max(1, Math.ceil((at - Date.now()) / 1000));
const inMins = (at) => Math.max(1, Math.ceil((at - Date.now()) / 60000));

function ledgerBegin(user, book) {
  try {
    const id = beginAttempt({ ownerUserId: user.id, flow: 'slipsync', book: book.key, appVersion: APP_VERSION, platform: 'ios' });
    return { id, uid: user.id, book: book.key, stage: 'webview_open', warnings: [], responseReceived: false, httpStatus: null, done: false };
  } catch {
    Sentry.captureMessage('Attempt ledger: beginAttempt failed', tagged('ledger_begin'));
    return null;
  }
}
function ledgerStage(ledger, stage) {
  if (!ledger || ledger.done) return;
  try { ledger.stage = stage; advanceStage(ledger.id, stage); } catch { Sentry.captureMessage('Attempt ledger: advanceStage failed', tagged('ledger_advance')); }
}
function ledgerExit(ledger, exitId, ctx = {}) {
  if (!ledger || ledger.done) return;
  ledger.done = true;
  try {
    const coded = mapExit(exitId, { ...ctx, lastStage: ledger.stage, warnings: ledger.warnings, responseReceived: ledger.responseReceived });
    finishAttempt(ledger.id, coded);
    flushOwner(ledger.uid); // not awaited: the sync UI never waits on the ledger
    // PostHog (spec §4 step 12): a view of the same coded exit; the ledger stays the record. #1587: plus why --
    // the exit codes, the bridge's coded counters and the slip count. Every value is a code or a bounded count
    // (analytics-scrub enforces it); the phone has no other channel that reaches us.
    const b = ledger.bridge || {};
    const props = {
      book: ledger.book, outcome: coded.outcome, attempt_id: ledger.id,
      stage: coded.stage, exit_reason: coded.reason_code, http_status: coded.http_status,
      bridge_last: b.last ?? null, last_auth_reason: b.lastAuthReason ?? null, last_skip: b.lastSkip ?? null,
      injects: b.injects ?? null, auth_errors: b.authErrors ?? null, unbound: b.unbound ?? null,
      slip_count: ledger.slipCount ?? null,
    };
    // #1659: the server's result counts, on a complete exit only (same values as the ledger row).
    if (Number.isInteger(coded.failed_count)) {
      Object.assign(props, { imported_count: coded.imported_count, skipped_count: coded.skipped_count,
        failed_count: coded.failed_count, untracked_count: coded.untracked_count });
    }
    // A failed run also carries the book's coded answer (book-error-class.js), never the book's text.
    if (ctx.bookError) {
      props.error_class = ctx.bookError.errorClass;
      if (props.http_status == null && ctx.bookError.httpStatus !== null) props.http_status = ctx.bookError.httpStatus;
      if (Number.isInteger(ctx.bookError.rpcCode)) props.rpc_code = ctx.bookError.rpcCode; // #1711
    } else if (Number.isInteger(ctx.rpcCode)) {
      props.rpc_code = ctx.rpcCode; // #1714: DK's page-2 rejection on a partial run
    }
    // 2026-10-18 visibility: where the WebView was (trail: route names + bridge notes, all closed tokens) on every
    // run, and on a run that did not import, the book's last not-signed-in answer. analytics-scrub closes each again.
    if (Array.isArray(ctx.trail) && ctx.trail.length) props.trail = ctx.trail.join('>');
    if (typeof ctx.stoppedAt === 'string') props.stopped_at = ctx.stoppedAt;
    const a = ctx.bookAnswer;
    if (a && coded.outcome !== 'success' && coded.outcome !== 'partial') {
      props.book_reason = a.reason;
      if (props.http_status == null && Number.isInteger(a.status)) props.http_status = a.status;
      if (a.ctype) props.content_type = a.ctype;
      if (Array.isArray(a.keys) && a.keys.length) props.keys = a.keys.join(',');
    }
    track(EVENTS.SLIPSYNC_RUN_COMPLETED, props);
  } catch {
    Sentry.captureMessage('Attempt ledger: finishAttempt failed', tagged('ledger_finish'));
  }
}

export function useSlipSync({ user, onSyncComplete, book = mgmConfig }) {
  const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);
  const [syncDiagnostics, setSyncDiagnostics] = useState(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [resultSummary, setResultSummary] = useState(null);
  // #1226: how many betslips the book returned (after the 50 cap), for the progress UI.
  const [foundCount, setFoundCount] = useState(null);
  const [configuredState, setConfiguredState] = useState(() => readConfig(book));
  const [lastSyncedAt, setLastSyncedAt] = useState(() => readLastSynced(book));
  const [showStateSetup, setShowStateSetup] = useState(false);
  // Bridge hardening: the opening notice, and Reconnect keyed to the user and book that were refused (Ultron P1-4).
  const [syncNotice, setSyncNotice] = useState(null);
  const [reconnectInfo, setReconnectInfo] = useState(null);
  const [canReconnect, setCanReconnect] = useState(false);
  const userKey = user ? user.id : null;
  const visibleReconnect = reconnectInfo && reconnectInfo.book === book.key && reconnectInfo.userId === userKey ? reconnectInfo : null;
  useEffect(() => { if (reconnectInfo && !visibleReconnect) setReconnectInfo(null); }, [book.key, userKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const reconnectAt = visibleReconnect ? visibleReconnect.availableAt : undefined;
  useEffect(() => {
    if (reconnectAt === undefined) { setCanReconnect(false); return undefined; }
    if (!reconnectAt || reconnectAt <= Date.now()) { setCanReconnect(true); return undefined; }
    setCanReconnect(false);
    const t = setTimeout(() => setCanReconnect(true), reconnectAt - Date.now());
    return () => clearTimeout(t);
  }, [reconnectAt]);
  // #1415: the SlipSync disclosure. Shown when the user has not agreed at the current version; the sync resumes
  // after "I agree" only for the book that asked (consentAskRef), and only if the sheet was not dismissed.
  const [showConsent, setShowConsent] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [resumeKey, setResumeKey] = useState(null);
  const consentAskRef = useRef(null);
  // Codex P1 (#1476): a different SlipTrack user (or none) while the sportsbook page is open closes it, so the
  // previous user's page is not left in the new user's hands. The submit check below is the backstop.
  // Local Codex P1: a pending consent request or resume belongs to the user who saw the sheet, so an account
  // change drops it too.
  useEffect(() => {
    if (cancelBridgeRef.current) { cancelBridgeRef.current(); cancelBridgeRef.current = null; }
    consentAskRef.current = null;
    setShowConsent(false);
    setConsentError(null);
    setResumeKey(null);
  }, [userKey]);
  const mountedRef = useRef(true);
  // The user as of the latest render: an await inside triggerSync re-checks it (#1461 class, pre-Codex F2).
  const userIdRef = useRef(user ? user.id : null);
  userIdRef.current = user ? user.id : null;
  const syncInProgressRef = useRef(false);
  const cancelBridgeRef = useRef(null);
  const onSyncCompleteRef = useRef(onSyncComplete);
  onSyncCompleteRef.current = onSyncComplete;
  useAttemptLedgerFlush(user);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel active WebView bridge on unmount (Codex finding)
      if (cancelBridgeRef.current) {
        cancelBridgeRef.current();
        cancelBridgeRef.current = null;
      }
    };
  }, []);

  const pendingSyncRef = useRef(false);

  const saveStateConfig = useCallback((region) => {
    // BookSyncConfig contract permits validRegions: string[] | null (spec §5).
    if (!Array.isArray(book.validRegions) || !book.validRegions.includes(region)) return;
    try {
      // Merge into the per-book shape, preserving other books' entries and the
      // legacy { betmgm_state } key (leave-legacy migration).
      const raw = localStorage.getItem(LS_CONFIG);
      let existing = {};
      if (raw) { try { const p = JSON.parse(raw); if (p && typeof p === 'object') existing = p; } catch { /* overwrite corrupt */ } }
      localStorage.setItem(LS_CONFIG, JSON.stringify({ ...existing, [book.key]: { region } }));
    } catch {
      Sentry.captureMessage('localStorage.setItem failed for slipsync_config', tagged('config_write'));
    }
    setConfiguredState(region);
    setShowStateSetup(false);
  }, [book]);

  const isIos = Capacitor.getPlatform() === 'ios';

  const triggerSync = useCallback(async () => {
    if (!isIos) return;
    if (!user) return;
    // Allow retry from terminal states (idle, success, error). Block during active states.
    const isTerminal = syncStatus === SYNC_STATUS.IDLE
      || syncStatus === SYNC_STATUS.SUCCESS
      || syncStatus === SYNC_STATUS.ERROR;
    if (!isTerminal || syncInProgressRef.current || consentSavesInFlight > 0) return;
    syncInProgressRef.current = true;
    const askedKey = book.key;
    // #1415 spec §9: consent first, then the region gate; the session owner (plan D6) is reset under the sync guard's
    // lock (bridge hardening P2-12), right before the browser.
    // opening_webview shows while they run (a stalled read is bounded at 10 s inside featureConsent).
    setSyncStatus(SYNC_STATUS.OPENING_WEBVIEW);
    setErrorMessage(null);
    // #1509: every entry point (the sheet, the My Bets cards, Reconnect, the consent and region resumes) comes
    // through here, so an account the server has not approved stops before consent and before any sportsbook
    // sign-in. Checked on every tap (an approval takes effect at once); unknown proceeds, because /api/slipsync
    // refuses an unapproved sync itself.
    // Codex r1 P1: the consent-save generation is captured before the FIRST pre-browser await, so a save that starts
    // and finishes during the access check is still detected below (one resume, never two WebViews).
    const saveGenAtRead = consentSaveGeneration;
    const access = await checkSlipSyncAccess(user.id);
    if (!mountedRef.current) { syncInProgressRef.current = false; return; }
    if (userIdRef.current !== user.id) { syncInProgressRef.current = false; setSyncStatus(SYNC_STATUS.IDLE); return; }
    if (access && access.allowed === false) {
      syncInProgressRef.current = false;
      track(EVENTS.SLIPSYNC_REFUSED, { book: book.key, reason: 'access_denied' });
      setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(accessDeniedMessage(access.status));
      return;
    }
    let consent;
    try {
      consent = await readFeatureConsent(supabase, user.id, 'slipsync');
    } catch {
      Sentry.captureMessage('SlipSync consent check failed', { level: 'warning', tags: { function: 'useSlipSync', stage: 'consent' } });
      consent = 'error';
    }
    if (!mountedRef.current) { syncInProgressRef.current = false; return; }
    if (userIdRef.current !== user.id) { syncInProgressRef.current = false; setSyncStatus(SYNC_STATUS.IDLE); return; }
    // Another card started saving an agreement while this read was in flight (Codex r4/r5 class, pre-Codex re-run):
    // the read may have seen that grant, so this tap opens nothing; the saving card resumes its own sync.
    if (consentSavesInFlight > 0 || consentSaveGeneration !== saveGenAtRead) { syncInProgressRef.current = false; setSyncStatus(SYNC_STATUS.IDLE); return; }
    if (consent !== 'granted') {
      syncInProgressRef.current = false;
      if (consent === 'missing') {
        consentAskRef.current = { key: askedKey, userId: user.id };
        setConsentError(null); setShowConsent(true); setSyncStatus(SYNC_STATUS.IDLE);
        return;
      }
      setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(ERROR_MESSAGES.CONSENT_CHECK_FAILED);
      return;
    }
    // Change B: per-book error strings take precedence over the shared fallback
    // constants. MGM sets errorMessages: null → falls back to the shared strings,
    // so MGM output is unchanged.
    const msgs = {
      authError: ERROR_MESSAGES.AUTH_EXPIRED,
      apiError: ERROR_MESSAGES.BETMGM_API_ERROR,
      openFailed: ERROR_MESSAGES.OPEN_FAILED,
      noBetsFound: ERROR_MESSAGES.NO_BETS_FOUND,
      // #885 capture-stage copy; import books keep the shared strings.
      captureNotSaved: null,
      submitTimeout: null,
      ...(book.errorMessages || {}),
    };

    // Change A: auto-detect books (needsRegionSelection === false, e.g. DK) skip the
    // region gate entirely. The injected script derives the region internally
    // (window.__siteExperience), so we open the WebView directly with a null region.
    let state = null;
    if (book.needsRegionSelection !== false) {
      state = readConfig(book);
      if (!state) {
        setShowStateSetup(true);
        // Resume sync automatically after state setup completes (Codex finding)
        pendingSyncRef.current = true;
        syncInProgressRef.current = false;
        setSyncStatus(SYNC_STATUS.IDLE);
        return;
      }
    }

    // Bridge hardening (spec §9): guard check (no side effects) -> kill switch -> guard take, then the window.
    const refusalCopy = (r) => (r.reason === 'in_flight' ? ERROR_MESSAGES.SYNC_IN_FLIGHT
      : r.reason === 'too_soon' ? fill(ERROR_MESSAGES.SYNC_TOO_SOON, { book: book.displayName, s: inSecs(r.retryAt) })
      : r.reason === 'take_a_break' ? fill(ERROR_MESSAGES.TAKE_A_BREAK, { book: book.displayName, m: inMins(r.retryAt) })
      : r.reason === 'cooldown' ? fill(ERROR_MESSAGES.RECONNECT_COOLDOWN, { m: inMins(r.retryAt) })
      : r.reason === 'reconnect_required' ? fill(ERROR_MESSAGES.RECONNECT_REQUIRED, { book: book.displayName })
      : r.reason === 'paused' ? fill(ERROR_MESSAGES.BOOK_PAUSED, { book: book.displayName })
      : msgs.openFailed);
    const refuse = (r) => {
      syncInProgressRef.current = false;
      track(EVENTS.SLIPSYNC_REFUSED, { book: book.key, reason: r.reason });
      if (!mountedRef.current) return;
      setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(refusalCopy(r));
      if (r.reason === 'cooldown' || r.reason === 'reconnect_required') setReconnectInfo({ userId: user.id, book: book.key, availableAt: r.retryAt || null });
    };
    const pre = guardCheck(user.id, book.key);
    if (!pre.ok) { refuse(pre); return; }
    setSyncStatus(SYNC_STATUS.OPENING_WEBVIEW); setErrorMessage(null);
    const enabled = await isBookEnabled(book.key);
    if (!mountedRef.current) { syncInProgressRef.current = false; return; }
    // The account changed during the read: nothing was taken yet, so drop the tap for the old user.
    if (userIdRef.current !== user.id) { syncInProgressRef.current = false; setSyncStatus(SYNC_STATUS.IDLE); return; }
    if (!enabled) { refuse({ reason: 'paused', retryAt: null }); return; }
    const g = tryStart(user.id, book.key);
    if (!g.ok) { refuse(g); return; }
    const guardToken = g.token;
    // #1415 plan D6 under the lock (bridge hardening P2-12): a second card cannot reset the sportsbook session while
    // this one opens. Nothing was opened yet, so a failure or an account change releases the lock as 'unopened'.
    let owner;
    try { owner = await ensureSportsbookSessionOwner(user.id); } catch {
      // session-reset's contract is "never throws": a throw is a defect, but the user can retry (CC-IMPL-037).
      Sentry.captureMessage('SlipSync session reset threw', { level: 'warning', tags: { function: 'useSlipSync', stage: 'session_reset' } });
      owner = { ok: false };
    }
    if (!mountedRef.current || userIdRef.current !== user.id || !owner.ok) {
      guardFinish(guardToken, 'unopened');
      syncInProgressRef.current = false;
      if (!mountedRef.current) return;
      if (userIdRef.current !== user.id) { setSyncStatus(SYNC_STATUS.IDLE); return; }
      setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(ERROR_MESSAGES.SESSION_RESET_FAILED);
      return;
    }
    const isCaptureBook = book.stage === 'capture';
    setReconnectInfo(null);
    setSyncNotice(fill(ERROR_MESSAGES.SYNC_NOTICE, { book: book.displayName }));

    const ledger = ledgerBegin(user, book);
    // Every terminal path calls exit(); the first code is the attempt's outcome for the ledger AND the guard
    // (spec §11, Ultron P1-5): no call site classifies, and the guard finishes once, in the finally below.
    let recordedExit = null;
    // Set once the bridge resolves: every later exit records the book's last answer and the trail (2026-10-18).
    let bridgeCtx = {};
    const exit = (code, ctx) => { if (recordedExit) return; recordedExit = code; ledgerExit(ledger, code, { ...bridgeCtx, ...ctx }); };
    try {
      setSyncStatus(SYNC_STATUS.OPENING_WEBVIEW);
      setErrorMessage(null);
      setResultSummary(null);
      setFoundCount(null);
      setNeedsReauth(false);
      setSyncDiagnostics(null);

      const { promise: bridgePromise, cancel } = openBookWebView(book, state);
      cancelBridgeRef.current = cancel;
      const webViewResult = await bridgePromise;
      cancelBridgeRef.current = null;
      const lastAnswer = webViewResult && webViewResult.lastAnswer ? webViewResult.lastAnswer : null;
      bridgeCtx = { bookAnswer: lastAnswer, trail: webViewResult && webViewResult.trail };

      if (!mountedRef.current) {
        syncInProgressRef.current = false;
        exit(EXIT.UNMOUNTED_AFTER_BRIDGE);
        return;
      }

      if (webViewResult.type === 'cancelled') {
        syncInProgressRef.current = false;
        // Local Codex P1: a cancel (an account change closes the page) returns to idle; OPENING_WEBVIEW is
        // non-terminal and would leave the new account with no sync button.
        setSyncStatus(SYNC_STATUS.IDLE);
        exit(EXIT.BRIDGE_CANCELLED);
        return;
      }

      // Store diagnostics for all terminal paths
      if (webViewResult.diagnostics) setSyncDiagnostics(webViewResult.diagnostics);
      // #1587: the bridge's coded counters and last trail step ride on the attempt's PostHog event.
      if (ledger) {
        const trail = Array.isArray(webViewResult.trail) ? webViewResult.trail : [];
        ledger.bridge = { ...(webViewResult.stats || {}), last: trail.length ? trail[trail.length - 1] : null };
      }

      if (webViewResult.type === 'close') {
        sendDiagnostics('close', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(ERROR_MESSAGES.SYNC_CANCELLED);
        syncInProgressRef.current = false;
        exit(EXIT.USER_CLOSED);
        return;
      }

      if (webViewResult.type === 'timeout') {
        sendDiagnostics('timeout', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(isCaptureBook ? ERROR_MESSAGES.SYNC_TIMEOUT : ERROR_MESSAGES.SIGNIN_TIMEOUT);
        Sentry.captureMessage('SlipSync timeout', tagged('webview_timeout', { trail: (webViewResult.trail || []).join('>').slice(0, 300) }));
        syncInProgressRef.current = false;
        exit(EXIT.WEBVIEW_TIMEOUT);
        return;
      }

      // 2026-10-18: signed in (off a sign-in route), the book kept answering something other than bets. The user is
      // told with the code; the ledger and PostHog get the answer and the trail (device Sentry receives nothing).
      if (webViewResult.type === 'book_no_bets') {
        sendDiagnostics('book_no_bets', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        const code = lastAnswer && Number.isInteger(lastAnswer.status) ? lastAnswer.status : (lastAnswer && lastAnswer.reason) || 'unknown';
        setErrorMessage(fill(ERROR_MESSAGES.BOOK_NO_BETS, { book: book.displayName, code }));
        const answerText = lastAnswer ? `${lastAnswer.reason} ${Number.isInteger(lastAnswer.status) ? lastAnswer.status : 'none'}` : 'none';
        Sentry.captureMessage('SlipSync book_no_bets', tagged('book_no_bets', { answer: answerText, trail: (webViewResult.trail || []).join('>').slice(0, 300) }));
        syncInProgressRef.current = false;
        exit(EXIT.BOOK_NO_BETS);
        return;
      }

      if (webViewResult.type === 'open_error') {
        sendDiagnostics('open_error', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(msgs.openFailed);
        Sentry.captureMessage('SlipSync open failed', tagged('open_failed', { error: sanitizeError(webViewResult.error) }));
        syncInProgressRef.current = false;
        exit(EXIT.OPEN_ERROR);
        return;
      }

      if (webViewResult.type === 'script_error') {
        sendDiagnostics('script_error', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(msgs.apiError);
        Sentry.captureMessage('SlipSync script error', tagged('script_error', { error: sanitizeError(webViewResult.error), trail: (webViewResult.trail || []).join('>').slice(0, 300) }));
        syncInProgressRef.current = false;
        exit(EXIT.BRIDGE_SCRIPT_ERROR);
        return;
      }

      // type === 'message': parse the bridge result
      ledgerStage(ledger, 'book_auth');
      const parsed = parseBetslipsMessage(webViewResult.detail);

      if (parsed.type === 'auth_error') {
        sendDiagnostics('auth_error', webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(msgs.authError);
        syncInProgressRef.current = false;
        exit(EXIT.AUTH_ERROR);
        return;
      }

      if (parsed.type === 'api_error' || parsed.type === 'script_error') {
        sendDiagnostics(parsed.type, webViewResult);
        setSyncStatus(SYNC_STATUS.ERROR);
        // Terminal, non-retryable discriminator: a valid-but-unsupported sportsbook
        // state (spec §7 step 4). Surface the book-specific "not available in your
        // state" copy instead of the generic recoverable "try again" message.
        const apiMsg = (parsed.type === 'api_error'
          && parsed.error === 'not_available_in_state'
          && msgs.notAvailableInState)
          ? msgs.notAvailableInState
          : msgs.apiError;
        setErrorMessage(apiMsg);
        Sentry.captureMessage('SlipSync book error', tagged('book_error', { type: sanitizeError(parsed.type), error: sanitizeError(parsed.error), trail: (webViewResult.trail || []).join('>').slice(0, 300) }));
        const bookError = classifyBookError(parsed);
        syncInProgressRef.current = false;
        exit(parsed.type === 'api_error' && parsed.error === 'not_available_in_state' ? EXIT.NOT_AVAILABLE_IN_STATE : EXIT.PARSED_ERROR, { parsedType: parsed.type, bookError });
        return;
      }
      ledgerStage(ledger, 'parse');

      // #885 (ADR 006): a capture-stage book must yield 'captured' and an import book
      // must never; either mismatch is a script fault (the server re-checks the stage too).
      const isCapture = book.stage === 'capture';
      if (isCapture !== (parsed.type === 'captured')) {
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(msgs.apiError);
        Sentry.captureMessage('SlipSync stage/result mismatch', tagged('stage_mismatch', { book: sanitizeError(book.key), type: sanitizeError(parsed.type) }));
        syncInProgressRef.current = false;
        exit(EXIT.PARSED_ERROR, { parsedType: 'script_error' });
        return;
      }

      let submitBody;
      if (isCapture) {
        if (parsed.pages.length === 0) {
          sendDiagnostics('capture_empty', webViewResult);
          setSyncStatus(SYNC_STATUS.ERROR);
          setErrorMessage(msgs.noBetsFound);
          syncInProgressRef.current = false;
          exit(EXIT.EMPTY);
          return;
        }
        submitBody = { book: book.key, stage: 'capture', pages: parsed.pages, meta: parsed.meta };
      } else {
        // type === 'betslips' or 'partial': proceed to trim/empty check
        // closeEvent was already removed in the messageFromWebview handler before reaching here
        // A read stopped at the cap is recorded by the ledger and PostHog (stopped_at) only: Sentry quota.
        if (parsed.type === 'partial' && parsed.stoppedAt != null && parsed.stoppedAt !== 'cap' && parsed.stoppedAt !== 'cap_25') {
          Sentry.captureMessage('SlipSync partial: stopped early', tagged('partial_stopped', { stoppedAt: typeof parsed.stoppedAt === 'number' ? parsed.stoppedAt : sanitizeError(parsed.stoppedAt) }, 'info'));
        }
        let betslips = parsed.data;
        if (ledger) ledger.slipCount = Array.isArray(betslips) ? Math.min(betslips.length, 1000) : 0; // #1587
        // #1712 PR A: the DK open-bet probe (diagnostic, never imported). A dropped one is a tagged info message.
        const openProbe = book.key === 'dk' && parsed.openProbe ? parsed.openProbe : undefined;
        if (book.key === 'dk' && parsed.openProbeDropped) Sentry.captureMessage('SlipSync open probe dropped', tagged('open_probe_dropped', null, 'info'));
        if (!Array.isArray(betslips) || betslips.length === 0) {
          if (openProbe) {
            // Settled list empty (all-open DK bets): send the probe alone, under the same account check as a submit,
            // before the instance is released. Its outcome never changes what the user sees.
            const probeResult = await postOpenProbeOnly(openProbe, { userId: user.id, userIdRef, mountedRef });
            if (probeResult === 'account_changed') {
              Sentry.captureMessage('SlipSync stopped: account changed before submit', { level: 'warning', tags: { function: 'useSlipSync', stage: 'user_changed' } });
              if (mountedRef.current) setSyncStatus(SYNC_STATUS.IDLE);
              syncInProgressRef.current = false;
              exit(EXIT.BRIDGE_CANCELLED);
              return;
            }
            if (probeResult === 'unmounted') {
              syncInProgressRef.current = false;
              exit(EXIT.UNMOUNTED_IN_SUBMIT);
              return;
            }
            // Pre-Codex fail-closed LOW: the account can change during the post; never paint this sync's
            // empty result on another account's sheet.
            if (userIdRef.current !== user.id) {
              if (mountedRef.current) setSyncStatus(SYNC_STATUS.IDLE);
              syncInProgressRef.current = false;
              exit(EXIT.BRIDGE_CANCELLED);
              return;
            }
          }
          setSyncStatus(SYNC_STATUS.ERROR);
          setErrorMessage(msgs.noBetsFound);
          syncInProgressRef.current = false;
          exit(EXIT.EMPTY);
          return;
        }
        if (betslips.length > 50) betslips = betslips.slice(0, 50);
        setFoundCount(betslips.length);
        submitBody = { book: book.key, betslips };
        // Pre-Codex fail-closed P2 (defence in depth): the probe never pushes a body over the route's cap.
        if (openProbe && utf8Bytes(JSON.stringify({ ...submitBody, openProbe })) <= SLIPSYNC_MAX_BODY_BYTES) {
          submitBody = { ...submitBody, openProbe };
        }
      }
      setSyncStatus(SYNC_STATUS.SUBMITTING);
      ledgerStage(ledger, 'submit');
      const submitAbort = new AbortController();
      const submitTimer = setTimeout(() => submitAbort.abort(), SUBMIT_TIMEOUT_MS);
      // The deadline must bound every await in the submit, including work inside
      // authedApiFetch that ignores the signal (a stalled session refresh) and the body
      // read, so each is raced against this rejection (#1229 pre-Codex F1/F2a).
      const deadline = new Promise((_, reject) => {
        submitAbort.signal.addEventListener('abort', () => reject(Object.assign(new Error('SlipSync submit timed out'), { name: 'AbortError' })));
      });
      deadline.catch(() => {});
      try {
        // Codex P1 (#1476), escape-log #1461: post only for the SlipTrack user this sync started for. authedApiFetch
        // sends the live session's token, and an auth change can land before the render, so the live session is
        // read here (inside the submit deadline) as well as the latest render's user. Unreadable or different:
        // nothing is posted, and the warning below covers CC-IMPL-010.
        // The checked session's token is the one sent (Codex r2 P1): check and send use the same snapshot.
        let sessionUserId = null;
        let sessionToken = null;
        try {
          const { data } = await Promise.race([deadline, supabase.auth.getSession()]);
          const s = data && data.session;
          sessionUserId = s && s.user ? s.user.id : null;
          sessionToken = s ? s.access_token || null : null;
        } catch (sessionErr) {
          if (submitAbort.signal.aborted) throw sessionErr;
        }
        if (userIdRef.current !== user.id || sessionUserId !== user.id || !sessionToken) {
          Sentry.captureMessage('SlipSync stopped: account changed before submit', { level: 'warning', tags: { function: 'useSlipSync', stage: 'user_changed' } });
          if (mountedRef.current) setSyncStatus(SYNC_STATUS.IDLE);
          syncInProgressRef.current = false;
          exit(EXIT.BRIDGE_CANCELLED);
          return;
        }
        const response = await Promise.race([deadline, authedApiFetch('/api/slipsync', {
          signal: submitAbort.signal,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-sliptrack-app-version': APP_VERSION,
          },
          body: JSON.stringify(submitBody),
        }, { accessToken: sessionToken })]);
        if (ledger) { ledger.responseReceived = true; ledger.httpStatus = response.status; }
        ledgerStage(ledger, 'server_response');

        if (!response.ok) {
          // #885 (Ultron delta G2): the server sends no Sentry event on a capture 400 and
          // device Sentry is off, so the response code goes out as a tagged warning, which
          // reaches PostHog client_warning {fn, stage}. The body read stays inside the submit
          // deadline; any failure there keeps code http_<status> and still exits HTTP_ERROR.
          // One body read, inside the submit deadline: capture rejections carry a code (#885), and a 403 may
          // carry stage 'consent' (#1415). Unreadable or late: body stays null and the generic copy below applies.
          // A capture book's coded warning below reports it; an import book's is reported here (CC-IMPL-010).
          let body = null;
          if (isCapture || response.status === 403) {
            try {
              body = await Promise.race([deadline, response.json()]);
            } catch {
              body = null;
              if (!isCapture) Sentry.captureMessage('SlipSync error body unreadable', { level: 'warning', tags: { function: 'useSlipSync', stage: `http_${response.status}_body` } });
            }
          }
          if (isCapture) {
            const code = body && typeof body.code === 'string' && /^[a-z_A-Z]{1,40}$/.test(body.code) ? body.code : `http_${response.status}`;
            Sentry.captureMessage('SlipSync capture rejected', { level: 'warning', tags: { function: 'slipsync_capture', stage: code } });
            sendDiagnostics(`capture_${code}`, webViewResult);
          }
          if (response.status === 403 && body && body.stage === 'consent') {
            // The server holds no slipsync row for this user: drop the cached grant and ask again.
            forgetFeatureConsent(user.id, 'slipsync');
            Sentry.captureMessage('SlipSync refused: consent required', { level: 'warning', tags: { function: 'useSlipSync', stage: 'consent_403' } });
            if (mountedRef.current) {
              consentAskRef.current = { key: book.key, userId: user.id };
              setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(ERROR_MESSAGES.CONSENT_REQUIRED); setShowConsent(true);
            }
            syncInProgressRef.current = false;
            exit(EXIT.HTTP_ERROR, { status: response.status });
            return;
          }
          if (mountedRef.current) {
            if (response.status === 401) setNeedsReauth(true);
            const msg = response.status === 401 ? ERROR_MESSAGES.SESSION_EXPIRED
              : response.status === 429 ? ERROR_MESSAGES.RATE_LIMITED
              : response.status === 400 ? ERROR_MESSAGES.INVALID_DATA
              : response.status === 403 ? ERROR_MESSAGES.NOT_AVAILABLE
              : response.status === 503 ? (isCapture && msgs.captureNotSaved ? msgs.captureNotSaved : ERROR_MESSAGES.UNAVAILABLE)
              : ERROR_MESSAGES.SERVER_ERROR;
            setSyncStatus(SYNC_STATUS.ERROR);
            setErrorMessage(msg);
          }
          syncInProgressRef.current = false;
          exit(EXIT.HTTP_ERROR, { status: response.status });
          return;
        }

        let data;
        try {
          data = await Promise.race([deadline, response.json()]);
        } catch (parseErr) {
          if (submitAbort.signal.aborted) throw parseErr;
          Sentry.captureMessage('SlipSync /api/slipsync response.json() parse failed', tagged('response_parse'));
          if (ledger) ledger.warnings.push('response_parse_failed');
          data = null;
        }

        const syncedAt = new Date().toISOString();
        // A capture imports nothing: no last-synced stamp and no bets refresh (#885).
        if (!isCapture) {
          try {
            // Merge into the per-book shape { <book.key>: iso }, preserving other books.
            const raw = localStorage.getItem(LS_LAST_SYNCED);
            let existing = {};
            if (raw) { try { const p = JSON.parse(raw); if (p && typeof p === 'object') existing = p; } catch { /* overwrite legacy bare string */ } }
            localStorage.setItem(LS_LAST_SYNCED, JSON.stringify({ ...existing, [book.key]: syncedAt }));
          } catch {
            Sentry.captureMessage('localStorage.setItem failed for slipsync_last_synced_at', tagged('last_synced_write'));
            if (ledger) ledger.warnings.push('last_synced_write_failed');
          }

          // Refresh bets list even if unmounted — server wrote the rows (Codex P2)
          // Bounded by the same deadline (Codex P1 on #1231): a hung refresh must not hold
          // the sheet in SUBMITTING. The server already answered, so the result still shows.
          if (onSyncCompleteRef.current) {
            try {
              await Promise.race([deadline, Promise.resolve().then(() => onSyncCompleteRef.current())]);
            } catch {
              Sentry.captureMessage(submitAbort.signal.aborted
                ? 'SlipSync onSyncComplete refresh timed out'
                : 'SlipSync onSyncComplete callback failed', tagged(submitAbort.signal.aborted ? 'callback_timeout' : 'callback_failed'));
              if (ledger) ledger.warnings.push(submitAbort.signal.aborted ? 'callback_timeout' : 'callback_failed');
            }
          }
        }

        // #1659: the server's counts go on the ledger row (mapExit drops any malformed set whole); a capture has none.
        // The server omits untracked_legs when zero.
        const counts = !isCapture && data && typeof data === 'object'
          ? { imported: data.imported, skipped: data.skipped, failed: data.failed, untracked: data.untracked_legs ?? 0 }
          : null;
        exit(EXIT.COMPLETE, { partial: parsed.type === 'partial', status: ledger ? ledger.httpStatus : null, stoppedAt: parsed.type === 'partial' ? parsed.stoppedAt : undefined, rpcCode: parsed.page2Code, counts });
        if (mountedRef.current) {
          const isCount = (v) => Number.isInteger(v) && v >= 0;
          if (isCapture) {
            setResultSummary(data && isCount(data.captured) ? { captured: data.captured } : { unknown: true, capture: true });
          } else {
            setLastSyncedAt(syncedAt);
            // Counts render as user-facing copy (#1226): anything but a non-negative integer
            // makes the whole summary unknown rather than "-1 new bets". Missing skipped/failed
            // (undefined/null) still default to 0 (P2-7).
            const skipped = data && data.skipped != null ? data.skipped : 0;
            const failed = data && data.failed != null ? data.failed : 0;
            if (data && isCount(data.imported) && isCount(skipped) && isCount(failed)) {
              // Never-reject PR 3: the top-level untracked_legs count (PR 4 contract). Present
              // only when a valid positive count arrives; an old server or a bad value adds nothing.
              const untracked = isCount(data.untracked_legs) ? data.untracked_legs : 0;
              setResultSummary(untracked > 0 ? { imported: data.imported, skipped, failed, untracked } : { imported: data.imported, skipped, failed });
            } else {
              setResultSummary({ unknown: true });
            }
          }
          setSyncStatus(SYNC_STATUS.SUCCESS);
        }
      } catch (e) {
        if (submitAbort.signal.aborted) {
          // #1229: the submit hit SUBMIT_TIMEOUT_MS. The server may still write the bets,
          // so refresh the list and say so; the user can retry. Recoverable: warning.
          Sentry.captureMessage('SlipSync submit timed out', tagged('submit_timeout'));
          // Unlock first; the refresh is best-effort and must not hold the sheet (F2b).
          if (mountedRef.current) {
            setSyncStatus(SYNC_STATUS.ERROR);
            setErrorMessage(isCapture && msgs.submitTimeout ? msgs.submitTimeout : ERROR_MESSAGES.SUBMIT_TIMEOUT);
          }
          syncInProgressRef.current = false;
          // A capture writes no bets, so there is nothing to refresh (A10).
          if (!isCapture && onSyncCompleteRef.current) {
            Promise.resolve()
              .then(() => onSyncCompleteRef.current && onSyncCompleteRef.current())
              .catch(() => Sentry.captureMessage('SlipSync onSyncComplete callback failed', tagged('callback_failed')));
          }
          exit(EXIT.SUBMIT_DEADLINE, { status: ledger ? ledger.httpStatus : null });
          return;
        }
        if (!mountedRef.current) {
          syncInProgressRef.current = false;
          exit(EXIT.UNMOUNTED_IN_SUBMIT);
          return;
        }
        exit(EXIT.SUBMIT_THROW);
        setSyncStatus(SYNC_STATUS.ERROR);
        setErrorMessage(ERROR_MESSAGES.NETWORK_ERROR);
        // P2-1: network errors are recoverable — use captureMessage, not captureException
        Sentry.captureMessage('SlipSync network error', tagged('network_error', { error: sanitizeError(e.message) }));
      } finally {
        clearTimeout(submitTimer);
      }
      syncInProgressRef.current = false;
    } finally {
      // §6a row 21: any path that did not record an exit (a future branch, an unexpected throw). That path also
      // never reset the instance, so release it and say the sync failed (pre-Codex).
      const unrecorded = !recordedExit;
      exit(EXIT.UNKNOWN);
      syncInProgressRef.current = false;
      if (unrecorded && mountedRef.current) { setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(msgs.openFailed); }
      guardFinish(guardToken, outcomeClassOf(recordedExit, { isCapture: isCaptureBook }));
      if (mountedRef.current) {
        setSyncNotice(null);
        const after = guardPeek(user.id, book.key);
        if (after && after.failures >= FAILURES_FOR_RECONNECT && after.cooldownUntil > Date.now()) {
          setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(fill(ERROR_MESSAGES.RECONNECT_COOLDOWN, { m: inMins(after.cooldownUntil) }));
          setReconnectInfo({ userId: user.id, book: book.key, availableAt: after.cooldownUntil });
        } else if (after && after.breakUntil > Date.now()) {
          setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(fill(ERROR_MESSAGES.TAKE_A_BREAK, { book: book.displayName, m: inMins(after.breakUntil) }));
        }
      }
    }
  }, [isIos, user, syncStatus, book]);

  const acceptConsent = useCallback(async () => {
    // Each request is its own object (Codex r3 P1): the agreement resumes only the request it answered. A request
    // dismissed, cancelled by a book change, or replaced by another book's request while saving resumes nothing.
    const ask = consentAskRef.current;
    // One agreement saves at a time across every instance (local Codex r2): a second card waits, so the first save's
    // resume is not refused by the second save's lock.
    if (!user || consentBusy || !ask || consentSavesInFlight > 0) return;
    setConsentBusy(true); setConsentError(null);
    consentSavesInFlight += 1;
    consentSaveGeneration += 1;
    let r;
    try {
      r = await recordFeatureConsent(supabase, user.id, 'slipsync');
    } finally {
      consentSavesInFlight -= 1;
    }
    if (!mountedRef.current) return;
    setConsentBusy(false);
    if (consentAskRef.current !== ask) return;
    if (r !== 'saved') { setConsentError(r === 'rejected' ? ERROR_MESSAGES.CONSENT_REJECTED : ERROR_MESSAGES.CONSENT_SAVE_FAILED); return; }
    consentAskRef.current = null;
    setShowConsent(false);
    setResumeKey({ key: ask.key, userId: ask.userId });
  }, [user, consentBusy]);

  const declineConsent = useCallback(() => { consentAskRef.current = null; setShowConsent(false); setConsentError(null); }, []);

  // Review Focus 4 (Codex r3 P1): binding the hook to another book cancels a pending consent request, at every
  // entry point (the Import sheet's book chips stay tappable while the disclosure is up).
  const boundBookKey = book.key;
  useEffect(() => {
    consentAskRef.current = null;
    setShowConsent(false);
    setConsentError(null);
  }, [boundBookKey]);

  // Resume only for the book that asked (Ultron P1-5): a sheet re-bound to another book drops the resume.
  useEffect(() => {
    if (!resumeKey) return;
    setResumeKey(null);
    // Only for the book AND the user that asked (local Codex P1: the save can land after an account change).
    if (resumeKey.key === book.key && user && resumeKey.userId === user.id) triggerSync();
  }, [resumeKey, book, user, triggerSync]);

  // Auto-trigger sync after first-run state setup completes (Codex finding)
  useEffect(() => {
    if (pendingSyncRef.current && configuredState && !showStateSetup) {
      pendingSyncRef.current = false;
      triggerSync();
    }
  }, [configuredState, showStateSetup, triggerSync]);

  const reconnect = useCallback(async () => {
    if (!user || !visibleReconnect) return;
    const r = guardReconnect(user.id, book.key);
    if (!r.ok) {
      setSyncStatus(SYNC_STATUS.ERROR); setErrorMessage(fill(ERROR_MESSAGES.RECONNECT_COOLDOWN, { m: inMins(r.retryAt) }));
      setReconnectInfo({ userId: user.id, book: book.key, availableAt: r.retryAt });
      return;
    }
    setReconnectInfo(null);
    await triggerSync();
  }, [user, book, visibleReconnect, triggerSync]);

  return {
    syncStatus, errorMessage, resultSummary, foundCount, lastSyncedAt, needsReauth,
    triggerSync, showStateSetup, saveStateConfig, configuredState, isIos,
    syncDiagnostics,
    showConsent, consentBusy, consentError, acceptConsent, declineConsent,
    syncNotice, reconnectInfo: visibleReconnect, canReconnect, reconnect,
  };
}
